import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Loader2, Mail } from 'lucide-react'
import { z } from 'zod'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FormField } from '@/components/ui/form-field'
import { meQuery, useResetPasswordMutation, useSendPasswordResetCodeMutation } from '@/api/auth'
import { ApiError } from '@/api/errors'

/**
 * /forgot-password — public route.
 *
 * Email a six-digit code, then set a new password with it. The backend
 * answers 204 even for unknown emails, so the copy stays deliberately
 * vague ("if that email has an account…").
 */

const forgotPasswordFormSchema = z
  .object({
    email: z.string().email('Enter a valid email'),
    code: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code'),
    password: z.string().min(8, 'Must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((values) => values.password === values.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
  })

type ForgotPasswordForm = z.infer<typeof forgotPasswordFormSchema>

export const Route = createFileRoute('/forgot-password')({
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery())
      throw redirect({ to: '/dashboard' })
    } catch (err) {
      if (err instanceof ApiError && err.isUnauthorized) return
      throw err
    }
  },
  component: ForgotPasswordPage,
})

function ForgotPasswordPage() {
  const navigate = useNavigate()
  const resetPassword = useResetPasswordMutation()
  const sendCode = useSendPasswordResetCodeMutation()
  const [secondsLeft, setSecondsLeft] = useState(0)
  const form = useForm<ForgotPasswordForm>({
    resolver: zodResolver(forgotPasswordFormSchema),
    defaultValues: { email: '', code: '', password: '', confirmPassword: '' },
  })

  useEffect(() => {
    if (secondsLeft <= 0) return
    const timer = window.setInterval(() => setSecondsLeft((value) => Math.max(value - 1, 0)), 1000)
    return () => window.clearInterval(timer)
  }, [secondsLeft])

  const requestCode = () => {
    const email = form.getValues('email').trim()
    const parsed = z.string().email('Enter a valid email').safeParse(email)
    if (!parsed.success) {
      form.setError('email', { message: parsed.error.issues[0]?.message ?? 'Enter a valid email' })
      return
    }
    sendCode.mutate(
      { email },
      {
        onSuccess: () => {
          setSecondsLeft(60)
          toast.success('If that email has an account, a code is on its way')
        },
        onError: (err) => toast.error(err.message || 'Could not send the code'),
      },
    )
  }

  const onSubmit = ({ confirmPassword: _, ...values }: ForgotPasswordForm) => {
    resetPassword.mutate(values, {
      onSuccess: () => {
        toast.success('Password updated — sign in with the new one')
        void navigate({ to: '/login' })
      },
      onError: (err) => {
        if (err instanceof ApiError) {
          if (err.fields) {
            for (const [field, message] of Object.entries(err.fields)) {
              if (field === 'email' || field === 'code' || field === 'password') {
                form.setError(field, { message })
              }
            }
            return
          }
        }
        toast.error(err.message || 'Could not reset the password')
      },
    })
  }

  return (
    <div className="grid min-h-screen place-items-center bg-surface-muted px-4 py-8">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3">
          <img src="/fuku-logo.svg" alt="Fuku AI" width={80} height={26} className="h-12 w-auto" />
          <h1 className="text-2xl font-bold tracking-tight text-ink">Reset your password</h1>
          <p className="text-center text-sm text-muted">
            We&apos;ll email you a code to pick a new one.
          </p>
        </div>

        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-4 rounded-[var(--radius-card)] border border-divider bg-surface-base p-6 shadow-card"
        >
          <FormField label="Email" htmlFor="email" error={form.formState.errors.email?.message}>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              aria-invalid={!!form.formState.errors.email}
              {...form.register('email')}
            />
          </FormField>

          <FormField
            label="Reset code"
            htmlFor="code"
            hint="The code expires in 10 minutes."
            error={form.formState.errors.code?.message}
          >
            <div className="flex gap-2">
              <Input
                id="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                aria-invalid={!!form.formState.errors.code}
                className="min-w-0 flex-1"
                {...form.register('code')}
              />
              <Button
                type="button"
                variant="secondary"
                className="min-w-28 shrink-0"
                disabled={sendCode.isPending || secondsLeft > 0}
                onClick={requestCode}
              >
                {sendCode.isPending ? (
                  <Loader2 className="animate-spin" aria-hidden="true" />
                ) : secondsLeft > 0 ? (
                  `Resend in ${secondsLeft}s`
                ) : (
                  <>
                    <Mail aria-hidden="true" />
                    Send code
                  </>
                )}
              </Button>
            </div>
          </FormField>

          <FormField
            label="New password"
            htmlFor="password"
            error={form.formState.errors.password?.message}
          >
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              aria-invalid={!!form.formState.errors.password}
              {...form.register('password')}
            />
          </FormField>

          <FormField
            label="Confirm password"
            htmlFor="confirmPassword"
            error={form.formState.errors.confirmPassword?.message}
          >
            <Input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              aria-invalid={!!form.formState.errors.confirmPassword}
              {...form.register('confirmPassword')}
            />
          </FormField>

          <Button
            type="submit"
            disabled={resetPassword.isPending}
            aria-busy={resetPassword.isPending}
            className="mt-2"
          >
            {resetPassword.isPending ? (
              <>
                <Loader2 className="animate-spin" aria-hidden="true" />
                Updating…
              </>
            ) : (
              'Set new password'
            )}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-muted">
          Remembered it?{' '}
          <Link to="/login" className="font-medium text-primary hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
